import { useState, useCallback, useRef } from 'react';
import { SubscriptionData, PricingPlan, PaymentDetails, PaymentRecord, Payment } from '../types/profile';
import subscriptionApiService, { 
  MobileMoneyPendingResponse, 
  getProviderTelecomCode,
  formatPhoneNumber,
  TELECOM_PROVIDERS
} from '../services/subscriptionApi';
import { useToast } from '../hooks/useToast';
import { useOnceEffect } from './useOnceEffect';
import { Subscription, CustomerTokenBalance, PlanFeatures } from '../types/subscription';

// Type pour le résultat d'un paiement
export interface PaymentResult {
  success: boolean;
  subscription?: SubscriptionData;
  pendingPayment?: MobileMoneyPendingResponse;
  error?: string;
}

// Fonction pour convertir les données API en format attendu par l'UI
const convertApiSubscriptionToUI = (
  apiSubscription: Subscription,
  tokenBalance?: CustomerTokenBalance,
  payments?: Payment[]
): SubscriptionData => {
  return {
    id: apiSubscription.id,
    userId: apiSubscription.customerId,
    currentPlan: apiSubscription.plan.name,
    tokenBalance: tokenBalance ? tokenBalance.remainingTokens : 0,
    tokenTotal: tokenBalance ? tokenBalance.totalTokens : 0,
    paymentHistory: payments ? payments.map((payment: Payment): PaymentRecord => ({
      id: payment.id,
      date: payment.date ? new Date(payment.date).toISOString().split('T')[0] : new Date(payment.createdAt).toISOString().split('T')[0],
      amount: payment.amount,
      method: payment.method || String(payment.paymentMethod) || 'N/A',
      plan: payment.plan || 'N/A',
      status: payment.status || (payment.paymentStatus === 'completed' ? 'Payé' : payment.paymentStatus === 'pending' ? 'En attente' : 'Échoué'),
      receiptUrl: payment.receiptUrl || payment.proofUrl || '#'
    })) : []
  };
};

// Fonction pour convertir les features de l'API en format attendu par l'UI
const convertApiFeaturesToUI = (apiFeatures: PlanFeatures): string[] => {
  const features: string[] = [];
  
  Object.entries(apiFeatures).forEach(([key, value]) => {
    if (value.enabled) {
      let feature = value.description || key.replace(/_/g, ' ');
      if (value.limit) {
        feature += ` (${value.limit})`;
      }
      features.push(feature);
    }
  });
  
  return features;
};

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const toast = useToast();
  // ✅ Utiliser useRef pour éviter les boucles infinies avec toast dans les dépendances
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const loadSubscriptionData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🌐 Récupération des données d\'abonnement depuis l\'API...');
      const apiData = await subscriptionApiService.getCurrentSubscription();
      
      const tokenBalance = await subscriptionApiService.getTokenBalance();
      
      // 🆕 Charger l'historique des paiements d'abonnement spécifique
      // via GET /subscriptions/current/payment-history
      const subscriptionPayments = await subscriptionApiService.getSubscriptionPaymentHistory();
      
      // Convertir vers le format PaymentRecord pour l'UI
      const payments: Payment[] = subscriptionPayments.map(item => ({
        id: item.transactionId,
        customerId: apiData.customerId,
        amount: parseFloat(item.amount),
        currency: item.currency,
        paymentMethod: item.telecom ? 'mobile' : 'card',
        paymentStatus: item.status === 'success' ? 'completed' : item.status,
        createdAt: item.createdAt,
        completedAt: item.completedAt,
        // Propriétés UI
        date: item.completedAt || item.createdAt,
        method: item.telecom ? `Mobile Money (${item.telecom})` : 'Carte bancaire',
        plan: item.planName,
        status: item.status === 'success' ? 'Payé' : item.status === 'pending' ? 'En attente' : 'Échoué'
      }));
      
      const subscriptionData = convertApiSubscriptionToUI(apiData, tokenBalance, payments);
      
      setSubscription(subscriptionData);
      console.log('✅ Données d\'abonnement récupérées avec succès');
    } catch (err) {
      const error = err as Error;
      const errorMessage = error.message || 'Impossible de récupérer les données d\'abonnement';
      setError(errorMessage);
      console.error('❌ Erreur API abonnement:', error);
      
      toastRef.current.showToast?.({
        type: 'error',
        title: 'Erreur de connexion',
        message: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  }, []); // ✅ Pas de dépendances - stable

  const loadPlans = useCallback(async () => {
    try {
      console.log('🌐 Récupération des plans depuis l\'API...');
      const apiPlans = await subscriptionApiService.getPlans();
      
      // ⚠️ Conserver l'UUID du plan (plan.id) pour POST /subscriptions/purchase
      const availablePlans = apiPlans.map(plan => ({
        id: plan.id,                                    // 🔑 UUID requis pour le backend
        name: plan.name,
        description: plan.description || '',
        price: (plan.monthlyPriceUSD || 0).toString(),
        features: convertApiFeaturesToUI(plan.features),
        configId: plan.configId,
        customerType: plan.customerType
      }));
      
      setPlans(availablePlans);
      console.log('✅ Plans récupérés avec succès:', availablePlans.map(p => ({ id: p.id, name: p.name })));
    } catch (err) {
      const error = err as Error;
      console.error('❌ Erreur récupération des plans:', error);
      
      toastRef.current.showToast?.({
        type: 'error',
        title: 'Erreur',
        message: 'Impossible de charger les plans disponibles'
      });
    }
  }, []); // ✅ Pas de dépendances - stable

  // Charger les données une seule fois
  useOnceEffect(() => {
    loadSubscriptionData();
    loadPlans();
  });

  const changePlan = async (
    newPlanId: string, 
    paymentMethod: 'card' | 'mobile' | 'manual', 
    paymentDetails?: PaymentDetails
  ): Promise<PaymentResult> => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('📤 Création d\'abonnement via l\'API...');
      console.log('📦 Données envoyées:', { newPlanId, paymentMethod, paymentDetails });
      
      // ✅ Validation: le planId doit être un UUID, pas un nom ou configId
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(newPlanId)) {
        console.error('❌ Format planId invalide:', newPlanId, '(UUID attendu)');
        throw new Error(`Format de plan ID invalide. Un UUID est requis (reçu: "${newPlanId}"). Veuillez rafraîchir la page et réessayer.`);
      }
      
      // ============================================
      // 📱 MOBILE MONEY - Utiliser le nouvel endpoint /subscriptions/purchase
      // ============================================
      if (paymentMethod === 'mobile' && paymentDetails) {
        console.log('📱 Achat via Mobile Money (nouvel endpoint)...');
        
        // Formater le numéro de téléphone au format backend (243XXXXXXXXX)
        const clientPhone = formatPhoneNumber(
          paymentDetails.countryCode || '243',
          paymentDetails.mobileNumber || paymentDetails.mobileMoneyNumber || ''
        );
        
        // Convertir le provider en code télécom (AM, OM, MP, AF)
        const telecom = getProviderTelecomCode(paymentDetails.mobileMoneyProvider || 'airtel');
        
        // Appeler le nouvel endpoint POST /subscriptions/purchase
        // 🆕 Inclure billingPeriod et promoCode depuis paymentDetails
        const purchaseResponse = await subscriptionApiService.purchasePlan({
          planId: newPlanId,
          clientPhone,
          telecom,
          channel: 'merchant', // Le système envoie la demande au téléphone
          billingPeriod: paymentDetails.billingPeriod || 'monthly',
          promoCode: paymentDetails.promoCode || undefined
        });
        
        // Le backend retourne toujours status: pending pour Mobile Money
        if (purchaseResponse.success && purchaseResponse.data) {
          const { transactionId, instructions } = purchaseResponse.data;
          
          console.log('📱 Paiement Mobile Money initié:', {
            transactionId,
            telecom,
            instructions
          });
          
          // Construire l'objet MobileMoneyPendingResponse pour le hook de polling
          const pendingResponse: MobileMoneyPendingResponse = {
            id: transactionId,
            planId: newPlanId,
            status: 'pending',
            transactionId,
            paymentStatus: 'pending',
            message: instructions || `Confirmez le paiement sur votre téléphone via ${TELECOM_PROVIDERS[telecom]?.ussd || '*150#'}`,
            checkStatusUrl: `/subscriptions/payment-status/${transactionId}`,
            expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(), // 5 minutes
            provider: TELECOM_PROVIDERS[telecom]?.name || paymentDetails.mobileMoneyProvider,
            phoneNumber: clientPhone,
            instructions,
            telecom
          };
          
          return {
            success: false, // En attente de confirmation
            pendingPayment: pendingResponse
          };
        } else {
          throw new Error('Échec de l\'initiation du paiement Mobile Money');
        }
      }
      
      // ============================================
      // 💳 CARTE BANCAIRE - Ancien workflow
      // ============================================
      let apiResponse: Subscription;
      
      if (paymentDetails && paymentMethod === 'card') {
        console.log('💳 Création d\'abonnement avec carte bancaire...');
        
        // Extraction des informations de la carte
        let expiryMonth: number | undefined;
        let expiryYear: number | undefined;
        if (paymentDetails.expiryDate && paymentDetails.expiryDate.includes('/')) {
          const parts = paymentDetails.expiryDate.split('/');
          expiryMonth = parseInt(parts[0], 10);
          expiryYear = parseInt(parts[1], 10);
          if (expiryYear < 100) expiryYear += 2000;
        }
        
        apiResponse = await subscriptionApiService.createSubscription({
          planId: newPlanId,
          paymentMethod: {
            type: 'card',
            cardNumber: paymentDetails.cardNumber,
            expiryMonth: expiryMonth,
            expiryYear: expiryYear,
            cvc: paymentDetails.cvv,
            name: 'Card Holder'
          }
        });
        
      } else {
        // Simple changement de plan sans paiement immédiat
        apiResponse = await subscriptionApiService.changeSubscriptionPlan({
          planId: newPlanId
        });
      }
      
      // Paiement direct réussi (carte ou changement de plan)
      const apiData = apiResponse as Subscription;
      
      // Récupérer les données mises à jour
      const tokenBalance = await subscriptionApiService.getTokenBalance();
      const payments = (await subscriptionApiService.getPaymentHistory()).payments;
      
      const updatedSubscription = convertApiSubscriptionToUI(apiData, tokenBalance, payments);
      
      setSubscription(updatedSubscription);
      console.log('✅ Abonnement créé/modifié avec succès');
      
      toastRef.current.showToast?.({
        type: 'success',
        title: 'Paiement réussi',
        message: `Votre abonnement ${newPlanId} a été activé avec succès`
      });
      
      return {
        success: true,
        subscription: updatedSubscription
      };
    } catch (err) {
      const error = err as Error;
      const errorMessage = error.message || 'Échec du paiement';
      setError(errorMessage);
      console.error('❌ Erreur lors du paiement:', error);
      
      toastRef.current.showToast?.({
        type: 'error',
        title: 'Échec du paiement',
        message: errorMessage
      });
      
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Note: Les tokens sont maintenant intégrés aux plans d'abonnement
  // L'achat de tokens séparé n'est plus supporté par l'API
  // Voir: ENDPOINTS_EXACT.md - Section "9. Tokens (Gestion intégrée)"
  const rechargeTokens = async (_paymentDetails: PaymentDetails) => {
    const errorMessage = 'L\'achat de tokens séparé n\'est plus disponible. Les tokens sont inclus dans votre plan d\'abonnement.';
    setError(errorMessage);
    
    toastRef.current.showToast?.({
      type: 'warning',
      title: 'Fonctionnalité désactivée',
      message: errorMessage
    });
    
    throw new Error(errorMessage);
  };

  // 🆕 Prévisualisation du prix avec validation backend des codes promo
  const previewPrice = async (params: {
    planId: string;
    billingPeriod?: 'monthly' | 'annual';
    promoCode?: string;
    isUpgrade?: boolean;
  }) => {
    try {
      const response = await subscriptionApiService.previewSubscriptionPrice(params);
      return response;
    } catch (err) {
      const error = err as Error;
      console.error('❌ Erreur preview prix:', error);
      return null;
    }
  };

  // 🆕 Upgrade vers un nouveau plan avec prorata automatique
  const upgradePlan = async (
    newPlanId: string,
    paymentDetails: PaymentDetails
  ): Promise<PaymentResult> => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('⬆️ Upgrade de plan via l\'API...');
      
      // Validation UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(newPlanId)) {
        throw new Error(`Format de plan ID invalide pour l'upgrade.`);
      }
      
      // Formater le numéro de téléphone
      const clientPhone = formatPhoneNumber(
        paymentDetails.countryCode || '243',
        paymentDetails.mobileNumber || paymentDetails.mobileMoneyNumber || ''
      );
      
      // Convertir le provider en code télécom
      const telecom = getProviderTelecomCode(paymentDetails.mobileMoneyProvider || 'airtel');
      
      // Appeler l'endpoint d'upgrade
      const upgradeResponse = await subscriptionApiService.upgradeSubscription({
        newPlanId,
        clientPhone,
        telecom,
        billingPeriod: paymentDetails.billingPeriod || 'monthly',
        promoCode: paymentDetails.promoCode || undefined,
        applyImmediately: true
      });
      
      if (upgradeResponse.success && upgradeResponse.data) {
        const { transactionId, instructions } = upgradeResponse.data;
        
        console.log('⬆️ Upgrade initié:', { transactionId, instructions });
        
        // Construire l'objet MobileMoneyPendingResponse pour le hook de polling
        const pendingResponse: MobileMoneyPendingResponse = {
          id: transactionId,
          planId: newPlanId,
          status: 'pending',
          transactionId,
          paymentStatus: 'pending',
          message: instructions || `Confirmez l'upgrade sur votre téléphone via ${TELECOM_PROVIDERS[telecom]?.ussd || '*150#'}`,
          checkStatusUrl: `/subscriptions/payment-status/${transactionId}`,
          expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
          provider: TELECOM_PROVIDERS[telecom]?.name || paymentDetails.mobileMoneyProvider,
          phoneNumber: clientPhone,
          instructions,
          telecom
        };
        
        return {
          success: false, // En attente de confirmation
          pendingPayment: pendingResponse
        };
      } else {
        throw new Error('Échec de l\'initiation de l\'upgrade');
      }
    } catch (err) {
      const error = err as Error;
      const errorMessage = error.message || 'Échec de l\'upgrade';
      setError(errorMessage);
      
      toastRef.current.showToast?.({
        type: 'error',
        title: 'Échec de l\'upgrade',
        message: errorMessage
      });
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    subscription,
    plans,
    isLoading,
    error,
    changePlan,
    upgradePlan,           // 🆕 Pour les upgrades
    previewPrice,          // 🆕 Pour la prévisualisation
    rechargeTokens,
    reload: loadSubscriptionData,
    refreshPlans: loadPlans
  };
};
